// Lib
import { useEffect } from 'react';

// Utils
import { colorBorders } from '../utils/tableCellFormattingUtils';

/**
 * Custom hook for managing table cell editing side effects
 * Handles syncing props to Handsontable instance and overriding undo/redo functionality
 *
 * @param {Object} props - Hook configuration
 */
export const useTableCellEditingEffects = (props) => {
    const {
        locale,
        filterQuery,
        isReadOnly,
        showTitle,
        showBottomPanel,
        isResizing,
        gridSize,
        isDarkMode,
        isSingleSelected,
        isUndoingOrRedoing,
        hotTableInstanceRef,
        dispatchUndoAction,
        dispatchRedoAction,
        dispatchFinishUndoingOrRedoing,
    } = props;

    /**
     * Update locale in Handsontable instance and re-render
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

        hotTableInstanceRef.current.milanoteProps.locale = locale;
        hotTableInstanceRef.current.render();
    }, [locale]);

    /**
     * Update filterQuery value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.filterQuery = filterQuery;
        hotTableInstanceRef.current.render();
    }, [filterQuery]);

    /**
     * Update settings for readonly table
     */
    useEffect(() => {
        if (!isReadOnly) return;

        // disable all shortcuts
        hotTableInstanceRef.current?.unlisten();
    }, [isReadOnly]);

    /**
     * Update showTitle value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.showTitle = showTitle;
    }, [showTitle]);

    /**
     * Update showBottomPanel value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.showBottomPanel = showBottomPanel;
    }, [showBottomPanel]);

    /**
     * Update isResizing value in the hotTableInstance
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.isResizing = isResizing;
    }, [isResizing]);

    /**
     * Update gridSize and destroy editor when grid size changes
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.gridSize = gridSize;
        hotTableInstanceRef.current.getActiveEditor()?.finishEditing();
        hotTableInstanceRef.current.deselectCell();
    }, [gridSize]);

    /**
     * Update relevant values with new darkmode setting
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.isDarkMode = isDarkMode;

        // recalculate the coloured cell borders with the new darkmode setting
        const dataArray = hotTableInstanceRef.current.getData();
        for (let row = 0; row < dataArray.length; row++) {
            for (let col = 0; col < dataArray[row].length; col++) {
                colorBorders(hotTableInstanceRef.current, row, col);
            }
        }
    }, [isDarkMode]);

    /**
     * Override Handsontable undo/redo functionality to use Milanote's instead
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        const undoRedoPluginInstance = hotTableInstanceRef.current.getPlugin('undoRedo');
        undoRedoPluginInstance.undo = () => {
            if (hotTableInstanceRef.current.isListening()) {
                dispatchUndoAction();
            }
        };
        undoRedoPluginInstance.redo = () => {
            if (hotTableInstanceRef.current.isListening()) {
                dispatchRedoAction();
            }
        };
    }, []);

    /**
     * isUndoingOrRedoing state persists as true in the state, and causes issues where changes to cell is cleared
     * after an undo/redo operation. So, always toggle this state back to false after an undo/redo operation on table.
     */
    useEffect(() => {
        if (isSingleSelected && isUndoingOrRedoing) dispatchFinishUndoingOrRedoing();
    }, [isSingleSelected, isUndoingOrRedoing]);
};

export default useTableCellEditingEffects;
